// -----------------------------------------------------------------------------
// LIST HELPERS (round 13, 04/Oct/26) — pure functions, unit-tested in tests/unit/lists.test.ts.
//   * header sorting: click cycles ↑ → ↓ → default order (A1); A4 flips ↑ / ↓ on Issue
//   * arrow-key movement through rows, card grids and menus (code list #8)
//   * running totals for the A2 Activity statistics graph
// -----------------------------------------------------------------------------

export type SortDir = "asc" | "desc";
export interface SortState<K extends string = string> { key: K | null; dir: SortDir }

/** Header click: same column → ↑ then ↓ then back to the default order; another column → ↑. */
export function nextSort<K extends string>(s: SortState<K>, key: K): SortState<K> {
  if (s.key !== key) return { key, dir: "asc" };
  if (s.dir === "asc") return { key, dir: "desc" };
  return { key: null, dir: "asc" };
}

/** Arrow shown in a sortable header: ↑ / ↓ on the sorted column, ↕ (grey) on the others. */
export const sortArrow = (s: SortState, key: string): "↑" | "↓" | "↕" => (s.key !== key ? "↕" : s.dir === "asc" ? "↑" : "↓");

type Value = string | number | null | undefined;
/** Stable sort by one column. Numbers compare as numbers, ISO dates as text (same order), text ignores case. Empty values go last. */
export function sortBy<T>(rows: T[], get: (r: T) => Value, dir: SortDir): T[] {
  const sign = dir === "asc" ? 1 : -1;
  return rows.map((r, i) => ({ r, i, v: get(r) })).sort((a, b) => {
    const av = a.v, bv = b.v;
    const ae = av === null || av === undefined || av === "", be = bv === null || bv === undefined || bv === "";
    if (ae || be) return ae === be ? a.i - b.i : ae ? 1 : -1;
    const c = typeof av === "number" && typeof bv === "number" ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    return c !== 0 ? c * sign : a.i - b.i;
  }).map((x) => x.r);
}

/** Roles from highest to lowest (A1 "My role" sorts by rank, not by name). */
export const ROLE_RANK = ["Super admin", "GM", "General manager", "Project head", "BIM Coordinator", "BIM Modeller"];
export const roleRank = (role: string | null): number => { const i = role ? ROLE_RANK.indexOf(role) : -1; return i < 0 ? ROLE_RANK.length : i; };

/** ↑ / ↓ through a list of n rows. Nothing highlighted yet: ↓ starts at the top, ↑ at the bottom. */
export function moveIndex(i: number | null, n: number, key: string): number | null {
  if (n === 0) return null;
  if (key === "ArrowDown") return i === null ? 0 : Math.min(n - 1, i + 1);
  if (key === "ArrowUp") return i === null ? n - 1 : Math.max(0, i - 1);
  if (key === "Home") return 0;
  if (key === "End") return n - 1;
  return i;
}

/** ↑ ↓ ← → through a grid of cards laid out `cols` per row (A1 cards view). */
export function moveGrid(i: number | null, n: number, cols: number, key: string): number | null {
  if (n === 0) return null;
  if (i === null) return key === "ArrowUp" || key === "ArrowLeft" ? n - 1 : 0;
  const step = key === "ArrowRight" ? 1 : key === "ArrowLeft" ? -1 : key === "ArrowDown" ? cols : key === "ArrowUp" ? -cols : 0;
  const j = i + step;
  return j < 0 || j >= n ? i : j;
}

/**
 * Running totals for the Activity statistics graph (option B): `days` holds one count per day and starts
 * 89 days before the range (project_activity_series). Result: for each day in the range, the total of
 * the `window` days up to and including it.
 */
export function runningTotals(days: number[], window: number, lead = 89): number[] {
  const out: number[] = [];
  let sum = 0;
  for (let k = 0; k < days.length; k++) {
    sum += days[k];
    if (k - window >= 0) sum -= days[k - window];
    if (k >= lead) out.push(sum);
  }
  return out;
}

/** A tidy y-axis top and step for counts up to `max` (about 4–6 grid lines). */
export function niceScale(max: number): { top: number; step: number } {
  for (const step of [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000]) {
    if (max / step <= 5) return { step, top: step * Math.max(1, Math.ceil(max / step)) };
  }
  const step = 10 ** Math.ceil(Math.log10(max / 5));
  return { step, top: step * Math.ceil(max / step) };
}

/** Date-range presets of the Activity statistics picker → ISO from/to (to = today). */
export type RangePreset = "30" | "90" | "180" | "year" | "start";
export function presetRange(p: RangePreset, today: string, first: string | null): { from: string; to: string } {
  const t = new Date(`${today}T00:00:00Z`);
  const minus = (n: number) => new Date(t.getTime() - n * 86400000).toISOString().slice(0, 10);
  if (p === "30") return { from: minus(29), to: today };
  if (p === "90") return { from: minus(89), to: today };
  if (p === "180") return { from: minus(182), to: today };
  if (p === "year") return { from: `${today.slice(0, 4)}-01-01`, to: today };
  const earliest = first && first < today ? first : minus(89);
  return { from: earliest < minus(731) ? minus(731) : earliest, to: today };
}
